// Rendering the calendar day a contract date DENOTES.
//
// A period of performance, a revision's effectivity, a CLIN association window
// and a deliverable's due date are DATE-ONLY values: the document says
// "2026-01-26", a day, with no time and no place. The wire carries them as
// instants anyway, anchored at UTC midnight (pangaea's esParseTime reads a bare
// "2006-01-02" as UTC), because there is no date-only type on the way here.
//
// Formatting that instant in the viewer's zone moves it. At UTC-05:00 that
// midnight is 7pm on the 25th, so a contract that starts on the 26th renders as
// starting on the 25th — a day the document never says, shown to the person
// whose job is to trust it. Two US coasts read the same contract differently.
//
// So: read these back in the zone they were anchored in. That is not a display
// preference, it is the inverse of the transform that stored them.
//
// NOT FOR TIMESTAMPS. created_at, uploaded_at, "last synced" are real instants
// — a moment that happened somewhere — and the viewer's local zone is the right
// place to read those. Using this on one would be the same bug pointed the
// other way.
/** What the delivery surfaces show where a date is absent. */ export const NO_CONTRACT_DATE = '—';
// YYYY-MM-DD, optionally followed by a time that MUST end in Z or ±HH:MM.
const CONTRACT_DATE_STRING = /^(\d{4})-(\d{2})-(\d{2})(?:T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,9})?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d))?$/;
function toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        // Two forms only, and anything else is unreadable (null → the absent
        // rendering), never a guess:
        //   - the day itself, "2026-01-26", which the Date constructor reads as
        //     UTC midnight — the anchor this module reads back;
        //   - an instant that names its zone, "…T12:00:00Z" or "…-05:00".
        // A time with NO zone is refused: the constructor reads it in the runner's
        // zone, so one string would denote a different day on each coast. So is
        // every non-ISO shape ("01/26/2026") the constructor would read locally.
        const match = CONTRACT_DATE_STRING.exec(trimmed);
        if (!match) return null;
        // The constructor rolls "2026-02-30" into Mar 2 without complaint. An
        // impossible day is a defect in the source, so the parsed day must be the
        // day that was written.
        const [year, month, day] = [
            Number(match[1]),
            Number(match[2]),
            Number(match[3])
        ];
        const written = new Date(Date.UTC(year, month - 1, day));
        if (written.getUTCFullYear() !== year || written.getUTCMonth() !== month - 1 || written.getUTCDate() !== day) {
            return null;
        }
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value.toDate === 'function') {
        try {
            const parsed = value.toDate();
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        } catch  {
            return null;
        }
    }
    return null;
}
/**
 * "Jan 26, 2026" — the day the value denotes, in every viewer's zone.
 *
 * Absence renders as an em dash rather than a guess or a blank cell: a date the
 * record does not state is a fact about the record.
 */ export function formatContractDate(value) {
    const date = toDate(value);
    if (!date) return NO_CONTRACT_DATE;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    });
}
/** The ISO calendar day, for keys, comparisons and `<input type="date">`. */ export function contractDateISO(value) {
    const date = toDate(value);
    return date ? date.toISOString().slice(0, 10) : '';
}
/**
 * Whole calendar days from today to the day `value` denotes — `0` today,
 * negative once it is past, `null` when there is no readable date.
 *
 * Formatting was only half the bug. A deadline is also COMPARED, and the same
 * UTC-midnight anchor breaks that comparison in the opposite direction: read
 * as an instant, a due date of today lands at 7pm YESTERDAY for a viewer at
 * UTC-05:00, so "is it past?" answers yes and the row goes overdue-red on the
 * morning it is actually due.
 *
 * So both sides are reduced to calendar days before they meet. The due day is
 * read in the zone it was anchored in (UTC, per this module's whole premise);
 * today is read in the viewer's own zone, because "today" is a fact about
 * where the reader is standing, not about where the value was stored. Neither
 * side is ever an instant, so no offset and no DST transition can move the
 * answer by a day.
 *
 * `today` is injectable so a test can state a day instead of inheriting the
 * runner's clock.
 */ export function contractDaysFromToday(value, today = new Date()) {
    const date = toDate(value);
    if (!date) return null;
    const dueDay = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((dueDay - todayDay) / 86400000);
}
